import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageBackground, StyleSheet, TouchableHighlight, View } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';
import { useIndexedStateReference, useNodeHandleFromIndex, useTimeDuration } from '@hooks';

import { VOD_Icons } from '../../../assets/icons/vod';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { FOCUS_NOW } from '../../../helpers/Tools';
import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCButtonContent } from '../../focusable/TWCButtonContent/TWCButtonContent';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'pictureDetailedPage';

const CONSTANTS = {
  width: Theme.px(544),
  height: Theme.px(306),
  borderRadius: Theme.px(12),
};

const BUTTONS_CONFIG = {
  PLAY: {
    index: 0,
    id: 'playButton',
  },
  RESET: {
    index: 1,
    id: 'resetButton',
  },
};

export const OnDemandEpisodeLayout = {
  TILE_HEIGHT: Theme.px(319),
  TILE_WIDTH: Theme.px(556),
  TILE_SPACING: Theme.px(30),
};

const episodeCompletedValue = 1; // Decimal values, 1 means 100%

const OnDemandEpisodeContent = ({ data, focused, t }) => {
  const { duration } = useTimeDuration(data.duration, 'seconds');
  const widthProgressBar = data?.watched
    ? 100 // indicates a completely watched episode.
    : data?.progress
    ? data.progress * 100 // get episode progress
    : 0; // no progress value by default

  return (
    <>
      <View
        style={[
          styles.tileContent,
          {
            borderRadius: focused ? Theme.px(6) : Theme.px(12),
          },
        ]}
      >
        <ImageBackground resizeMode={'cover'} source={{ uri: data.thumb }} style={styles.thumbnail} />
        <View
          style={[
            styles.tileInfo,
            {
              backgroundColor: focused ? Theme.withOpacity(Theme.black, 0.4) : Theme.transparent,
            },
          ]}
        >
          <View style={styles.texts}>
            <TWCText noTestID variant={'regularText29'}>
              {t('layout.seasonInitial', {
                season: data.season,
                episode: data.episode,
              })}
            </TWCText>
            <TWCText noTestID variant={'regularText29'}>
              {duration}
            </TWCText>
          </View>
          {data?.progress !== episodeCompletedValue && widthProgressBar !== 0 && (
            <View style={styles.progressWrapper}>
              <View
                style={[
                  styles.progress,
                  {
                    width: `${widthProgressBar}%`,
                  },
                ]}
              />
            </View>
          )}
        </View>
      </View>
    </>
  );
};

export const OnDemandEpisodeTile = forwardRef(
  (
    {
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      testID,
      onFocus,
      onPress,
      onPressPlay,
      onPressRestart,
      data,
      onBlur,
      setIsItemFocused,
      hideFocus,
    },
    reference,
  ) => {
    const { PLAY, RESET } = BUTTONS_CONFIG;
    const [isTileFocused, setFocused] = useState(false);
    const focused = isTileFocused && !hideFocus;

    const { t } = useTranslation('translation', {
      keyPrefix: tPath,
    });
    const showRestartButton = !data.completed && data.progress > 0 && data.progress < 1;
    const [buttonsStatesReferences, , setButtonsStatesReferences] = useIndexedStateReference({});

    let duration = data.duration;

    if (duration && data.point) {
      duration = duration - data.point;
    }
    if (duration && duration < 0) {
      duration = 0;
    }

    const minutes = TimeUtils.getMinutes(duration);

    const dataButtons = [
      {
        id: PLAY.id,
        title: t(showRestartButton ? 'episodeTile.resumeButton' : 'episodeTile.playButton'),
        accessibilityLabel: t(
          showRestartButton ? 'episodeTile.resumeButtonAccessibilityLabel' : 'episodeTile.playButtonAccessibilityLabel',
          {
            series: data.seriestitle,
            season: data.season,
            episode: data.episode,
            title: data.title,
            minutes,
            index: data.index + 1,
            length: data.length,
            description: data.description,
          },
        ),
        accessibilityHint: t(
          showRestartButton ? 'episodeTile.resumeButtonAccessibilityHint' : 'episodeTile.playButtonAccessibilityHint',
        ),
        icon: VOD_Icons.play,
        iconFocused: VOD_Icons.playFocused,
      },
    ];

    showRestartButton &&
      dataButtons.push({
        id: RESET.id,
        title: t('episodeTile.restartButton'),
        icon: VOD_Icons.reload,
        iconFocused: VOD_Icons.reloadFocused,
        accessibilityHint: t('episodeTile.restartButtonAccessibilityHint'),
        accessibilityLabel: t('episodeTile.restartButtonAccessibilityLabel'),
      });

    const playHandle = useNodeHandleFromIndex(buttonsStatesReferences, PLAY.index);
    const resetHandle = useNodeHandleFromIndex(buttonsStatesReferences, RESET.index);

    const handleFocused = isFocused => {
      setFocused(isFocused);
      setIsItemFocused && setIsItemFocused(isFocused);
    };

    // TODO fix this lint warning
    // eslint-disable-next-line react/no-unstable-nested-components
    const _renderItem = ({ accessibilityLabel, accessibilityHint, id, title, icon, iconFocused }, index) => {
      const itemNextFocusDown = index === PLAY.index && showRestartButton ? resetHandle : null;
      const itemNextFocusUp = index === RESET.index ? playHandle : nextFocusUp;

      return (
        <FocusableHighlight
          key={index}
          accessibilityHint={accessibilityHint}
          accessibilityLabel={accessibilityLabel}
          nextFocusDown={itemNextFocusDown}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          nextFocusUp={itemNextFocusUp}
          ref={buttonReference => {
            setButtonsStatesReferences(buttonReference, index);
          }}
          style={focused ? styles.button : styles.tileTarget}
          styleFocused={styles.buttonFocused}
          testID={`vod_episode_button_${index}`}
          underlayColor={Theme.transparent}
          onBlur={() => {
            if (!hideFocus) {
              handleFocused(false);
            }
          }}
          onFocus={() => {
            if (!hideFocus) {
              handleFocused(true);
            }
          }}
          onPress={() => {
            onPress && onPress(id);

            if (id === BUTTONS_CONFIG.PLAY.id) {
              onPressPlay?.();
            }

            if (id === BUTTONS_CONFIG.RESET.id) {
              onPressRestart?.();
            }
          }}
        >
          {focused ? (
            <TWCButtonContent
              icon={icon}
              iconFocused={iconFocused}
              progress={{
                animated: false,
                value: id === PLAY.id ? data.progress : null,
              }}
              testID={''}
              title={title}
            />
          ) : (
            <View />
          )}
        </FocusableHighlight>
      );
    };

    return (
      <>
        <View style={[styles.tileWrapper, focused && styles.tileFocused]}>
          {dataButtons.map((item, index) => _renderItem(item, index))}
          <TouchableHighlight
            accessible
            nativeID={testID}
            nextFocusDown={null}
            nextFocusLeft={nextFocusLeft}
            nextFocusRight={nextFocusRight}
            nextFocusUp={nextFocusUp}
            ref={reference}
            style={styles.focusTarget}
            testID={testID}
            onBlur={onBlur}
            onFocus={() => {
              onFocus && onFocus();
              FOCUS_NOW(buttonsStatesReferences.current[PLAY.index]);
            }}
          >
            <View />
          </TouchableHighlight>
        </View>
        <OnDemandEpisodeContent
          data={data}
          focused={focused}
          t={t}
          testID={`pdp_episode_$${data._id}`}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </>
    );
  },
);

OnDemandEpisodeTile.propTypes = {
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  testID: PropTypes.string,
  onFocus: PropTypes.func,
  onPress: PropTypes.func,
  onPressPlay: PropTypes.func,
  onPressRestart: PropTypes.func,
  data: PropTypes.object,
  onBlur: PropTypes.func,
  setIsItemFocused: PropTypes.func,
  hideFocus: PropTypes.bool,
};
OnDemandEpisodeTile.defaultProps = {
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
  testID: null,
  onFocus: () => {},
  onPress: () => {},
  onPressPlay: () => {},
  onPressRestart: () => {},
  data: null,
  onBlur: () => {},
  setIsItemFocused: () => {},
  hideFocus: false,
};

OnDemandEpisodeTile.displayName = 'OnDemandEpisodeTile';

const styles = StyleSheet.create({
  button: {
    backgroundColor: Theme.withOpacity(Theme.white, 0.15),
    borderRadius: Theme.px(12),
    height: Theme.px(84),
    marginBottom: Theme.px(10),
    minWidth: Theme.px(350),
    overflow: 'hidden',
    width: Theme.px(350),
  },
  buttonFocused: {
    backgroundColor: Theme.transparent,
  },
  buttonsWrapper: {
    alignItems: 'center',
    borderRadius: CONSTANTS.borderRadius,
    height: CONSTANTS.height,
    justifyContent: 'center',
    position: 'absolute',
    width: CONSTANTS.width,
  },
  focusTarget: {
    height: 1,
    opacity: 1,
    width: 1,
  },
  progress: {
    backgroundColor: Theme.dodgerLightBlue,
    height: '100%',
  },
  progressWrapper: {
    backgroundColor: Theme.withOpacity(Theme.suvaGrey, 0.2),
    bottom: 0,
    height: Theme.px(12),
    left: 0,
    position: 'absolute',
    width: CONSTANTS.width,
  },
  restartButton: {
    marginTop: Theme.px(15),
  },
  texts: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  thumbnail: {
    flex: 1,
  },
  tileContent: {
    height: CONSTANTS.height,
    marginLeft: Theme.px(6),
    marginTop: Theme.px(6),
    overflow: 'hidden',
    position: 'absolute',
    width: CONSTANTS.width,
    zIndex: 0,
  },
  tileFocused: {
    borderColor: Theme.white,
    borderWidth: Theme.px(6),
  },
  tileInfo: {
    borderRadius: CONSTANTS.borderRadius,
    height: CONSTANTS.height,
    justifyContent: 'flex-end',
    paddingBottom: Theme.px(23),
    paddingHorizontal: Theme.px(18),
    position: 'absolute',
    width: CONSTANTS.width,
  },
  tileTarget: {
    height: 1,
    width: 1,
  },
  tileWrapper: {
    alignItems: 'center',
    borderRadius: CONSTANTS.borderRadius,
    height: Theme.px(318),
    justifyContent: 'center',
    width: Theme.px(556),
    zIndex: 1,
  },
});
